import type { TFunction } from 'i18next';

/** Every exported file opens with the product's name, so the sheet says where it came from. */
const PREFIXO_DO_PRODUTO = 'CosturAI';

/** What a file name accepts: an account called "DRO Confecções" becomes `DRO_Confeccoes`. */
function paraNomeDeArquivo(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function diaDoArquivo(today: Date): string {
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * `CosturAI_DRO_carteira_2026-10-16.xlsx`. The product comes first so every sheet the
 * factory sends out says which system produced it, and the active account right after so
 * two accounts' files never land in the same folder under the same name. With no account
 * name known it is simply left out.
 */
export function nomeDoArquivo(conta: string, assunto: string, today: Date): string {
  const partes = [
    PREFIXO_DO_PRODUTO,
    paraNomeDeArquivo(conta),
    paraNomeDeArquivo(assunto),
    diaDoArquivo(today),
  ].filter((parte) => parte !== '');
  return `${partes.join('_')}.xlsx`;
}

/** One bold cell plus the empty ones that keep the row as wide as the table under it. */
type TLinhaDeCabecalho = Array<{ value: string; fontWeight: 'bold' } | null>;

/**
 * The line above the column headers: which account produced the file and when. Whoever opens
 * the spreadsheet a week later has no screen to look at, so the sheet has to say it itself.
 *
 * It is padded to the width of the table because a short first row makes the writer size
 * the sheet by it and drop the columns to its right.
 */
export function cabecalhoDaConta(conta: string, t: TFunction, today: Date, colunas: number): TLinhaDeCabecalho {
  const nome = conta.trim();
  const data = today.toLocaleDateString('pt-BR');
  const texto =
    nome === '' ? t('exportar.cabecalho', { data }) : t('exportar.cabecalhoComFabrica', { fabrica: nome, data });

  return [{ value: texto, fontWeight: 'bold' }, ...Array.from({ length: Math.max(0, colunas - 1) }, () => null)];
}
